import { FC, useRef } from "react";
import { useTranslation } from "react-i18next";

import facebook from "../../icons/facebook.svg";
import instagram from "../../icons/instagram.svg";
import telegram from "../../icons/telegram.svg";
import viber from "../../icons/viber.svg";
import email from "../../icons/email.svg";
import twitter from "../../icons/twitter.svg";

import { useClickOutOfElement } from "../../custom-hooks/base-hooks/useClickOutOfElement";
import { useBreakpoint } from "../../custom-hooks/useBreakpoint";

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    newsTitle: string;
    newsUrl: string;
}

const ShareModal: FC<ShareModalProps> = ({ isOpen, onClose, newsTitle, newsUrl }) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const { isMobile } = useBreakpoint();

    useClickOutOfElement(modalRef, onClose);

    if (!isOpen) return null;

    const shareOptions = [
        { name: "Telegram", icon: telegram, action: () => window.open(`https://t.me/share/url?url=${encodeURIComponent(newsUrl)}&text=${encodeURIComponent(newsTitle)}`) },
        { name: "Viber", icon: viber, action: () => window.open(`viber://forward?text=${encodeURIComponent(newsTitle + " " + newsUrl)}`) },
        { name: "Email", icon: email, action: () => (window.location.href = `mailto:?subject=${encodeURIComponent(newsTitle)}&body=${encodeURIComponent(newsUrl)}`) },
        { name: "Instagram", icon: instagram, action: () => alert(t("news.share.instagramApp")) },
        { name: "Facebook", icon: facebook, action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(newsUrl)}`) },
        { name: "Twitter", icon: twitter, action: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(newsTitle)}&url=${encodeURIComponent(newsUrl)}`) }
    ];

    const copyLink = () => {
        navigator.clipboard.writeText(newsUrl);
        alert(t("news.share.copied"));
        onClose();
    };

    return (
        <div
            className={`fixed inset-0 z-[100] flex justify-center ${isMobile ? "items-end" : "items-center bg-black/50"}`}
            style={
                isMobile
                    ? { background: "#0A2C5CCC", backdropFilter: "blur(4px)" }
                    : {}
            }
        >
            <div
                ref={modalRef}
                className={
                    `bg-background-100 ${isMobile ? "rounded-t-[20px] w-full p-6" : "rounded-[4px] w-full max-w-[490px] p-[24px]"} flex flex-col gap-[28px]`
                }
            >

                <div className="flex items-center justify-between">
                    <h2 className="text-[24px] font-medium text-primary-400 uppercase">
                        {t("news.share.title")}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-primary-400 hover:opacity-70 transition-opacity"
                    >
                        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                            <path d="M22 6L6 22M6 6l16 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                <div className="flex flex-col items-center w-full gap-[28px]">

                    <div className="grid grid-cols-2 gap-[20px] md:hidden">
                        {shareOptions.map((option) => (
                            <button
                                key={option.name}
                                onClick={option.action}
                                className="flex flex-col items-center justify-center w-[121px] h-[109px] border border-border-300 rounded-[6px] py-4 px-[15px] hover:border-primary-400 hover:bg-gray-50 transition-all gap-[8px]"
                            >
                                <img src={option.icon} width={40} height={40} />

                                <span className="font-geologica font-normal text-[14px] leading-[1.3] text-text-700 text-center">
                                    {option.name}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="hidden md:grid grid-cols-3 grid-rows-2 gap-x-[33px] gap-y-[24px]">
                        {shareOptions.map((option) => (
                            <button
                                key={option.name}
                                onClick={option.action}
                                className="
                w-[121px] h-[109px] flex flex-col items-center justify-center border border-border-300 rounded-[6px] px-[15px] pt-[16px] pb-[16px] hover:border-primary-400 hover:bg-gray-50 transition-all gap-[12px] shadow-[0px_4px_10px_rgba(0,0,0,0.08)]">
                                <img
                                    src={option.icon}
                                    width={32}
                                    height={32}
                                    className="object-contain"
                                />

                                <span
                                    className="font-geologica font-normal text-[14px] leading-[1.3] text-text-700 text-center">
                                    {option.name}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="flex flex-col md:hidden items-center gap-[24px]">
                        <input
                            type="text"
                            value={newsUrl}
                            readOnly
                            className="w-[262px] h-[54px] border border-border-300 rounded-[4px] font-geologica font-normal text-[16px] leading-[1.3] text-text-300 px-[20px] pt-[12px] pb-[14px] bg-gray-50"
                        />

                        <button
                            onClick={copyLink}
                            className="w-[182px] h-[54px] bg-primary-400 text-background-100 rounded-[4px] uppercase font-medium flex items-center justify-between px-[20px] pt-[16px] pb-[18px]"
                        >
                            {t("buttons.copy")}

                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <rect x="7" y="7" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                <path d="M4 13V5a1 1 0 011-1h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>

                    <div className="hidden md:flex flex-row gap-[10px]">
                        <input
                            type="text"
                            value={newsUrl}
                            readOnly
                            className="w-[262px] h-[54px] border border-border-300 rounded-l-[4px] font-geologica font-normal text-[16px] leading-[1.3] text-text-300 px-[20px] pt-[12px] pb-[14px] bg-gray-50"
                        />

                        <button
                            onClick={copyLink}
                            className="w-[182px] h-[54px] bg-primary-400 text-background-100 rounded-[4px] uppercase font-medium flex items-center justify-between px-[20px] pt-[16px] pb-[18px]"
                        >
                            {t("buttons.copy")}

                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <rect x="7" y="7" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                <path d="M4 13V5a1 1 0 011-1h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareModal;